// SessionIcons.tsx
import React from 'react';
import { HiOutlineArrowsRightLeft, HiOutlineArrowPath, HiOutlineHomeModern, HiOutlineSun } from 'react-icons/hi2';
import type { IconType } from 'react-icons';

// ---------- Heroicons-style custom SVGs (outline, rounded caps/joins) ----------

// 1) Calipers (for calibrated)
export const CalipersIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <g strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      {/* Upper jaw */}
      <path d="M8 4h8M8 7h6" />
      {/* Beam */}
      <path d="M8 4v12" />
      {/* Slider block */}
      <rect x="6" y="9" width="6" height="4" rx="1" />
      {/* Depth rod */}
      <path d="M8 16v4" />
      {/* Lower jaw (angled) */}
      <path d="M8 12l-2 2M8 12l2 2" />
      {/* Thumb wheel */}
      <circle cx="9" cy="11" r="0.75" />
    </g>
  </svg>
);

// 2) Tunnel arch (for Catesby)
export const TunnelIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <g strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      {/* Arch */}
      <path d="M5 19V11a7 7 0 0 1 14 0v8" />
      {/* Base */}
      <path d="M4 19h16" />
      {/* Perspective rails */}
      <path d="M8 19l2-4m6 4-2-4M10 15h4" />
    </g>
  </svg>
);

// 3) Tyre oval (vertically oriented) - HeroIcons style
export const TyreOvalIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <g strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      {/* Outer oval */}
      <ellipse cx="12" cy="12" rx="3" ry="5" />
      {/* Inner oval */}
      <ellipse cx="12" cy="12" rx="1.5" ry="3" />
    </g>
  </svg>
);

// 3) Tyre oval (vertically oriented) - HeroIcons style
export const TyreOvalIconSimple: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <g strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      {/* Outer oval */}
      <ellipse cx="12" cy="12" rx="3" ry="5" />
    </g>
  </svg>
);

// ---------- Badge wrapper to overlay a small badge icon in the corner ----------

type WithBadgeProps = {
  Main: IconType | React.ComponentType<any>;
  badge?: IconType | React.ComponentType<any>;
  badge2?: IconType | React.ComponentType<any>;
  size?: number; // px
  className?: string;
  badgeScale?: number; // Scale multiplier for badge size (default 0.45)
};

const WithBadge: React.FC<WithBadgeProps> = ({
  Main,
  badge: Badge,
  badge2: Badge2,
  size = 24,
  className,
  badgeScale = 0.45,
}) => {
  const badgeSize = Math.round(size * badgeScale);
  return (
    <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }} className={className}>
      <Main size={size} />
      {Badge && (
        <span
          style={{
            position: 'absolute',
            right: -8,
            bottom: -8,
            width: badgeSize,
            height: badgeSize,
          }}
        >
          <Badge size={badgeSize} width={badgeSize} height={badgeSize} />
        </span>
      )}
      {Badge2 && (
        <span
          style={{
            position: 'absolute',
            right: badgeSize * 0.3 - 8,
            bottom: -8,
            width: badgeSize,
            height: badgeSize,
          }}
        >
          <Badge2 size={badgeSize} width={badgeSize} height={badgeSize} />
        </span>
      )}
    </span>
  );
};

// ---------- Per-session icon components (consistent theme) ----------

export const TraverseIcon: React.FC<{ size?: number; className?: string }> = (p) => (
  <HiOutlineArrowsRightLeft size={p.size ?? 24} className={p.className} />
);

export const TraverseTyreReferenceIcon: React.FC<{
  size?: number;
  className?: string;
}> = ({ size = 24, className }) => (
  <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }} className={className}>
    <HiOutlineArrowsRightLeft size={size} />
    <TyreOvalIcon width={size} height={size} style={{ position: 'absolute', right: -12, bottom: -16 }} />
  </span>
);

export const TraverseTyreComparisonIcon: React.FC<{
  size?: number;
  className?: string;
}> = ({ size = 24, className }) => {
  const tyreSize = size;
  return (
    <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }} className={className}>
      {/* Traverse arrows - smaller, upper left */}
      <HiOutlineArrowsRightLeft size={size} />
      {/* First tyre oval - lower right */}
      <TyreOvalIcon width={tyreSize} height={tyreSize} style={{ position: 'absolute', right: -10, bottom: -16 }} />
      {/* Second tyre oval - overlapping, same vertical position */}
      <TyreOvalIcon width={tyreSize} height={tyreSize} style={{ position: 'absolute', right: -16, bottom: -18 }} />
    </span>
  );
};

export const TraverseIndoorsIcon: React.FC<{ size?: number; className?: string }> = ({ size, className }) => (
  <WithBadge Main={HiOutlineArrowsRightLeft} badge={HiOutlineHomeModern} size={size} className={className} />
);

export const TraverseCatesbyIcon: React.FC<{ size?: number; className?: string }> = ({ size = 24, className }) => (
  <TunnelIcon width={size} height={size} className={className} />
);

export const LoopIcon: React.FC<{ size?: number; className?: string }> = (p) => (
  <HiOutlineArrowPath size={p.size ?? 24} className={p.className} />
);

export const LoopVelodromeOutdoorsIcon: React.FC<{ size?: number; className?: string }> = ({ size, className }) => (
  <WithBadge Main={HiOutlineArrowPath} badge={HiOutlineSun} size={size} className={className} />
);

export const LoopVelodromeIndoorsIcon: React.FC<{ size?: number; className?: string }> = ({ size, className }) => (
  <WithBadge Main={HiOutlineArrowPath} badge={HiOutlineHomeModern} size={size} className={className} />
);

export const FreerideCalibratedIcon: React.FC<{ size?: number; className?: string }> = ({ size = 24, className }) => (
  <CalipersIcon width={size} height={size} className={className} />
);
